<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { qrImageUrl } from '@/utils/format'

const props = defineProps<{
  secret?: string
  otpauth?: string
  loading?: boolean
}>()

const emit = defineEmits<{ verify: [code: string] }>()

const { t } = useI18n()
const code = ref('')
const backupMode = ref(false)

const hint = computed(() =>
  backupMode.value ? t('auth.js.backup_code_hint') : t('auth.js.app_code_hint'),
)

const qrSrc = computed(() => (props.otpauth ? qrImageUrl(props.otpauth, 220) : ''))

function toggleBackup() {
  backupMode.value = !backupMode.value
  code.value = ''
}

function submit() {
  emit('verify', code.value.trim())
}

async function copySecret() {
  if (props.secret) await navigator.clipboard?.writeText(props.secret).catch(() => {})
}
</script>

<template>
  <div class="rounded-lg border border-dashed border-white-light p-4">
    <strong class="mb-2 block">{{ t('auth.step3_totp') }}</strong>

    <div v-if="secret || otpauth" class="mb-3 space-y-2">
      <div v-if="qrSrc" class="text-center">
        <img :src="qrSrc" alt="QR" class="mx-auto max-w-[220px] rounded border border-white-light p-1" />
      </div>
      <div v-if="secret">
        <label class="text-sm">{{ t('auth.secret') }}</label>
        <div class="flex gap-2">
          <input :value="secret" class="form-input flex-1 font-mono text-xs" readonly />
          <button type="button" class="btn btn-secondary btn-sm" @click="copySecret">📋</button>
        </div>
      </div>
    </div>

    <form class="space-y-2" @submit.prevent="submit">
      <label class="text-sm">{{ t('auth.enter_6digit') }}</label>
      <input
        v-model="code"
        class="form-input"
        :maxlength="backupMode ? 9 : 9"
        autocomplete="one-time-code"
        :inputmode="backupMode ? 'text' : 'numeric'"
        :placeholder="backupMode ? 'XXXX-XXXX' : '000000'"
      />
      <p class="text-xs text-white-dark">{{ hint }}</p>
      <button type="submit" class="btn btn-primary w-full" :disabled="loading">
        {{ t('auth.finish_login') }}
      </button>
      <button type="button" class="w-full text-center text-xs text-white-dark hover:text-primary" @click="toggleBackup">
        {{ backupMode ? t('auth.use_app_code') : t('auth.use_backup_code') }}
      </button>
    </form>
  </div>
</template>
